import { flashError } from '@brainfocus/web/flash';
import { useEventCallback } from '@brainfocus/web/utils/react/use_event_callback';
import { useFactory } from '@brainfocus/web/utils/react/use_factory';
import { useStableArray } from '@brainfocus/web/utils/react/use_stable_array';
import { useEffect, useState } from 'preact/hooks';
import { EMPTY, Subject, catchError, interval, merge, of, switchMap, tap, from } from 'rxjs';

/**
 * Load async resource returning object with the last loaded data and current.
 * The resource can be automatically kept up to date be recurrent updating over
 * time.
 */
export function useAsync<T>(
    factory: () => Promise<T>,
    inputs: unknown[],
    repeatInterval: number | null = null,
): AsyncResource<T> {
    /**
     * Currently loaded data
     */
    const [data, setData] = useState<T>();
    /**
     * Indicates the factory is currently in progress
     */
    const [loading, setLoading] = useState<boolean>(false);
    /**
     * Indicates last factory call failed and new data won't be pulled until
     * inputs change.
     */
    const [failed, setFailed] = useState<boolean>(false);
    /**
     * Signal for manual update
     */
    const update$ = useFactory(() => new Subject<void>());

    // The load restarts when an input changes (`===` each, as Preact compares deps), with
    // the factory and interval of that render; later renders do not swap them in.
    const stableInputs = useStableArray(inputs);
    const readOptions = useEventCallback(() => ({ factory, repeatInterval }));

    useEffect(() => {
        const { factory: load, repeatInterval: every } = readOptions();
        setData(undefined);

        const sub = merge(
            every !== null ? interval(every) : EMPTY,
            of(1),
            update$,
        ).pipe(
            tap(() => setLoading(true)),
            // catchError lives INSIDE switchMap so a single rejected factory()
            // only ends that one inner fetch — the merged source stays alive and
            // the next update$/poll tick re-fetches. A catchError on the outer
            // pipe would complete the whole subscription on the first error.
            switchMap(() => from(load()).pipe(
                catchError((e) => {
                    flashError('Something went wrong', e);
                    setFailed(true);
                    setLoading(false);
                    return EMPTY;
                }),
            )),
        ).subscribe((loadedData) => {
            setFailed(false);
            setData(loadedData);
            setLoading(false);
        });
        return () => sub.unsubscribe();
    }, [stableInputs, readOptions, update$]);

    return {
        data,
        loading: loading && !data,
        updating: loading,
        failed,
        update,
    };

    function update() {
        update$.next();
    }
}

/**
 * Current state of the async resource
 */
export interface AsyncResource<T> {
    /**
     * Last loaded data (if any)
     */
    data?: T;
    /**
     * Whether the resource is being loaded for the first time for given
     * inputs.
     */
    loading: boolean;
    /**
     * Whether the resource is pulled again just to keep it up to date
     */
    updating: boolean;
    /**
     * Indicates the resource pulling failed
     */
    failed: boolean;
    /**
     * When called a resource is immediately pulled again.
     */
    update: () => void;
}
